import React from 'react';
import { styled } from '@mui/material/styles';
import { tUi } from '../../../../../../../../../utils/dq_lc';
import { HtmlContentColumn, HtmlContentWrapper, SlideTitle } from '../../../../../../styles/LearnTab/slideBase.styles';
import { CaseSectionStack, CaseSection, CaseSectionLabel } from './CaseStudyCard.styles';
import { LabArtifact, LabArtifactBar } from './HandsOnLabCard.styles';
import { inline, listOf, textOf } from './HandsOnLabCard.comp';

// Example in a real system (ThemeFirst): one artifact that shows the idea of the slide before it — a JSON or
// MongoDB document, pseudocode or log lines as code, a flowchart as boxes and arrows, or a table — with notes on
// the parts that matter. Code is shown as written; the notes, flow and table are translated text.
const CODE_KINDS = ['json', 'pseudocode', 'log'];
const KINDS = [...CODE_KINDS, 'flow', 'table'];

const FlowList = styled('ol')`
  && {
    list-style: none;
    padding-left: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 8px;
  }

  li {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  li + li::before {
    content: '→';
    color: #0097B2;
    font-weight: 700;
    font-size: 20px;
  }
`;

const FlowBox = styled('div')`
  min-width: 120px;
  padding: 8px 12px;
  border: 1.5px solid #0097B2;
  border-radius: 8px;
  background: #F0FAFB;

  strong {
    display: block;
  }

  small {
    display: block;
    margin-top: 2px;
    color: #00687A;
    font-size: 12px;
    font-weight: 700;
  }
`;

const ArtifactTable = styled('table')`
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;

  th,
  td {
    padding: 6px 10px;
    border: 1px solid #D1D5DB;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: #F3F4F6;
  }
`;

const NoteList = styled('ul')`
  && {
    list-style: none;
    padding-left: 0;
    margin: 0;
  }

  li + li {
    margin-top: 8px;
  }

  code {
    padding: 1px 6px;
    border-radius: 4px;
    background: #F3F4F6;
    font-size: 14px;
  }
`;

export const ExampleArtifactCard = ({ slide, uiLocale = 'en-US' }) => {
  const origin = slide?._pbdq_origin || slide || {};
  const kind = KINDS.includes(textOf(origin.artifact_kind)) ? textOf(origin.artifact_kind) : '';
  const kindLabel = kind ? tUi(`slide.example.kind.${kind}`, uiLocale) : '';
  const title = textOf(origin.artifact_title) || kindLabel;
  const code = typeof origin.code === 'string' ? origin.code.replace(/\s+$/, '') : '';
  const flow = listOf(origin.flow).filter((step) => textOf(step?.label));
  const columns = listOf(origin.table_columns).map(textOf);
  const rows = listOf(origin.table_rows).map((row) => listOf(row?.cells).map(textOf));
  const notes = listOf(origin.callouts).filter((note) => textOf(note?.target) && textOf(note?.note));

  const renderArtifact = () => {
    if (CODE_KINDS.includes(kind) && code) {
      return (
        <LabArtifact data-example-artifact={kind}>
          <LabArtifactBar>
            <span>{title}</span>
            {title !== kindLabel && <span>{kindLabel}</span>}
          </LabArtifactBar>
          <pre><code>{code}</code></pre>
        </LabArtifact>
      );
    }
    if (kind === 'flow' && flow.length > 0) {
      return (
        <>
          <CaseSectionLabel>{title}</CaseSectionLabel>
          <FlowList data-example-artifact="flow">
            {flow.map((step, idx) => (
              <li key={idx}>
                <FlowBox>
                  <strong>{idx + 1}. {inline(step.label)}</strong>
                  {textOf(step.system) && <small>{textOf(step.system)}</small>}
                </FlowBox>
              </li>
            ))}
          </FlowList>
        </>
      );
    }
    if (kind === 'table' && columns.length > 0 && rows.length > 0) {
      return (
        <>
          <CaseSectionLabel>{title}</CaseSectionLabel>
          <ArtifactTable data-example-artifact="table">
            <thead><tr>{columns.map((col, idx) => <th key={idx}>{col}</th>)}</tr></thead>
            <tbody>
              {rows.map((cells, r) => (
                <tr key={r}>{columns.map((_, c) => <td key={c}>{inline(cells[c] || '')}</td>)}</tr>
              ))}
            </tbody>
          </ArtifactTable>
        </>
      );
    }
    return null;
  };

  const artifact = renderArtifact();
  return (
    <HtmlContentColumn data-example-kind={kind || 'none'}>
      {textOf(origin.headline) && <SlideTitle>{textOf(origin.headline)}</SlideTitle>}
      <HtmlContentWrapper>
        <CaseSectionStack>
          {textOf(origin.context) && (
            <CaseSection data-example-section="context">
              <CaseSectionLabel>{tUi('slide.example.inRealSystem', uiLocale)}</CaseSectionLabel>
              {inline(origin.context)}
            </CaseSection>
          )}
          {artifact && <CaseSection data-example-section="artifact">{artifact}</CaseSection>}
          {notes.length > 0 && (
            <CaseSection data-example-section="notes">
              <CaseSectionLabel>{tUi('slide.example.notice', uiLocale)}</CaseSectionLabel>
              <NoteList>
                {notes.map((note, idx) => (
                  <li key={idx}><code>{textOf(note.target)}</code> — {inline(note.note)}</li>
                ))}
              </NoteList>
            </CaseSection>
          )}
          {textOf(origin.takeaway) && (
            <CaseSection data-example-section="takeaway">
              <CaseSectionLabel>{tUi('slide.example.takeaway', uiLocale)}</CaseSectionLabel>
              {inline(origin.takeaway)}
            </CaseSection>
          )}
        </CaseSectionStack>
      </HtmlContentWrapper>
    </HtmlContentColumn>
  );
};

export default ExampleArtifactCard;
